import { useState, useEffect, useRef } from "react";
import { RefreshCw, Plus, Trash2, Upload, MessageSquare, Image, Clock, ChevronUp, ChevronDown, Sun, Moon, CloudSun } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../components/Toast";
import Card from "../components/Card";
import type { Form } from "../types";

interface AutomationStep {
  id?: number;
  step_order: number;
  step_type: "text" | "image";
  body: string;
  attachment_id: number | null;
  delay_minutes: number;
  day_offset: number;
  hour_from: number;
  hour_to: number;
  enabled: boolean;
}

interface Attachment {
  id: number;
  filename: string;
  mime_type: string;
  size_bytes: number;
}

const TIME_WINDOWS = [
  { label: "Mattina", icon: Sun, hour_from: 9, hour_to: 12, desc: "9:00 - 12:00" },
  { label: "Pomeriggio", icon: CloudSun, hour_from: 14, hour_to: 17, desc: "14:00 - 17:00" },
  { label: "Sera", icon: Moon, hour_from: 20, hour_to: 22, desc: "20:00 - 22:00" },
];

const DAY_OPTIONS = [
  { label: "Stesso giorno", value: 0 },
  { label: "Giorno dopo", value: 1 },
  { label: "2 giorni dopo", value: 2 },
  { label: "3 giorni dopo", value: 3 },
  { label: "7 giorni dopo", value: 7 },
];

export default function AutomationsPage() {
  const { toast } = useToast();
  const [forms, setForms] = useState<Form[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [steps, setSteps] = useState<AutomationStep[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchForms = async () => {
    try {
      const res = await api.get("/forms");
      if (res.data.ok) setForms(res.data.forms || []);
    } catch {}
  };

  const fetchSteps = async (formId: number) => {
    try {
      const res = await api.get(`/automations/forms/${formId}/steps`);
      if (res.data.ok) setSteps(res.data.steps || []);
    } catch {}
  };

  const fetchAttachments = async (formId: number) => {
    try {
      const res = await api.get(`/automations/forms/${formId}/attachments`);
      if (res.data.ok) setAttachments(res.data.attachments || []);
    } catch {}
  };

  useEffect(() => { fetchForms(); }, []);

  useEffect(() => {
    if (selectedFormId) {
      fetchSteps(selectedFormId);
      fetchAttachments(selectedFormId);
    } else {
      setSteps([]);
      setAttachments([]);
    }
  }, [selectedFormId]);

  const addStep = async (type: "text" | "image") => {
    if (!selectedFormId) return;
    const isFirst = steps.length === 0;
    try {
      const res = await api.post(`/automations/forms/${selectedFormId}/steps`, {
        step_type: type,
        body: "",
        day_offset: isFirst ? 0 : 1,
        hour_from: isFirst ? 0 : 14,
        hour_to: isFirst ? 0 : 17,
        enabled: true,
      });
      if (res.data.ok) {
        setSteps([...steps, res.data.step]);
        toast("Step aggiunto", "success");
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Errore", "error");
    }
  };

  const updateStep = async (index: number, updates: Partial<AutomationStep>) => {
    if (!selectedFormId) return;
    const step = steps[index];
    if (!step?.id) return;
    try {
      const res = await api.put(`/automations/forms/${selectedFormId}/steps/${step.id}`, updates);
      if (res.data.ok) {
        const newSteps = [...steps];
        newSteps[index] = res.data.step;
        setSteps(newSteps);
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Errore", "error");
    }
  };

  const deleteStep = async (index: number) => {
    if (!selectedFormId) return;
    const step = steps[index];
    if (!step?.id) return;
    try {
      await api.delete(`/automations/forms/${selectedFormId}/steps/${step.id}`);
      setSteps(steps.filter((_, i) => i !== index));
      toast("Step eliminato", "success");
    } catch (err: any) {
      toast(err.response?.data?.error || "Errore", "error");
    }
  };

  const moveStep = async (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;
    const newSteps = [...steps];
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    const order = newSteps.map(s => s.id).filter(Boolean) as number[];
    if (!selectedFormId || order.length === 0) return;
    try {
      const res = await api.put(`/automations/forms/${selectedFormId}/steps/reorder`, { order });
      if (res.data.ok) setSteps(res.data.steps);
    } catch {
      toast("Errore riordinamento", "error");
    }
  };

  const uploadAttachment = async (file: File) => {
    if (!selectedFormId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/automations/forms/${selectedFormId}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.ok) {
        setAttachments([res.data.attachment, ...attachments]);
        toast("File caricato", "success");
      }
    } catch (err: any) {
      toast(err.response?.data?.error || "Upload fallito", "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteAttachment = async (id: number) => {
    if (!selectedFormId) return;
    try {
      await api.delete(`/automations/forms/${selectedFormId}/attachments/${id}`);
      setAttachments(attachments.filter(a => a.id !== id));
      toast("Allegato eliminato", "success");
    } catch (err: any) {
      toast(err.response?.data?.error || "Errore", "error");
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getWindowLabel = (step: AutomationStep) => {
    if (step.step_order === 1) return "Subito (automatico)";
    const dayLabel = DAY_OPTIONS.find(d => d.value === step.day_offset)?.label || `+${step.day_offset} giorni`;
    const windowLabel = `${step.hour_from}:00 - ${step.hour_to}:00`;
    return `${dayLabel}, fascia ${windowLabel}`;
  };

  return (
    <div className="space-y-6" data-testid="automations-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Automazioni</h1>
        <p className="text-zinc-400 text-sm mt-1">Configura la scaletta di invio automatico WhatsApp per ogni form</p>
      </div>

      {/* Form Selector */}
      <Card title="Seleziona Form">
        <select
          value={selectedFormId || ""}
          onChange={(e) => setSelectedFormId(e.target.value ? Number(e.target.value) : null)}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-fuchsia-500"
          data-testid="form-selector"
        >
          <option value="">— Seleziona un form —</option>
          {forms.map((f) => (
            <option key={f.id} value={f.id}>{f.name} ({f.status})</option>
          ))}
        </select>
      </Card>

      {selectedFormId && (
        <>
          {/* Steps Builder */}
          <Card title="Scaletta di Invio">
            <div className="space-y-4">
              {steps.length === 0 ? (
                <p className="text-zinc-500 text-center py-6">Nessun step configurato. Aggiungi il primo messaggio.</p>
              ) : (
                steps.map((step, i) => {
                  const isFirst = i === 0;
                  return (
                    <div key={step.id || i} className="p-4 bg-zinc-900 rounded-xl border border-zinc-800 space-y-3" data-testid={`step-${i}`}>
                      {/* Header: step number + timing */}
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-8 h-8 rounded-full bg-fuchsia-600 flex items-center justify-center text-white font-bold text-sm">
                            {i + 1}
                          </div>
                          {i > 0 && (
                            <>
                              <button onClick={() => moveStep(i, "up")} disabled={i <= 1} className="text-zinc-500 hover:text-white disabled:opacity-20">
                                <ChevronUp size={14} />
                              </button>
                              <button onClick={() => moveStep(i, "down")} disabled={i === steps.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-20">
                                <ChevronDown size={14} />
                              </button>
                            </>
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {step.step_type === "text" ? <MessageSquare size={16} className="text-fuchsia-400" /> : <Upload size={16} className="text-fuchsia-400" />}
                            <span className="text-sm font-medium text-white">
                              {step.step_type === "text" ? "Messaggio" : "Allegato"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock size={14} className="text-zinc-500" />
                            <span className="text-xs text-zinc-400">{getWindowLabel(step)}</span>
                          </div>
                        </div>

                        {!isFirst && (
                          <button onClick={() => deleteStep(i)} className="text-zinc-500 hover:text-red-400 p-1" title="Elimina step">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      {/* Timing config (not for first step) */}
                      {!isFirst && (
                        <div className="grid grid-cols-2 gap-3 pl-11">
                          <div>
                            <label className="block text-xs text-zinc-500 mb-1">Quando</label>
                            <select
                              value={step.day_offset}
                              onChange={(e) => updateStep(i, { day_offset: Number(e.target.value) })}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-fuchsia-500"
                            >
                              {DAY_OPTIONS.map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-zinc-500 mb-1">Fascia oraria</label>
                            <div className="flex gap-1">
                              {TIME_WINDOWS.map(w => {
                                const isActive = step.hour_from === w.hour_from && step.hour_to === w.hour_to;
                                const Icon = w.icon;
                                return (
                                  <button
                                    key={w.label}
                                    onClick={() => updateStep(i, { hour_from: w.hour_from, hour_to: w.hour_to })}
                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs transition-colors ${
                                      isActive
                                        ? "bg-fuchsia-600 text-white"
                                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                                    }`}
                                    title={w.desc}
                                  >
                                    <Icon size={14} /> {w.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Message body */}
                      {step.step_type === "text" && (
                        <div className="pl-11">
                          <textarea
                            value={step.body}
                            onChange={(e) => updateStep(i, { body: e.target.value })}
                            placeholder="Scrivi il messaggio... usa {{nome}} per inserire campi del form"
                            rows={3}
                            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-fuchsia-500 resize-y"
                          />
                        </div>
                      )}

                      {/* Attachment step */}
                      {step.step_type === "image" && (
                        <div className="pl-11 space-y-2">
                          <textarea
                            value={step.body}
                            onChange={(e) => updateStep(i, { body: e.target.value })}
                            placeholder="Didascalia opzionale..."
                            rows={2}
                            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-fuchsia-500 resize-y"
                          />
                          <select
                            value={step.attachment_id || ""}
                            onChange={(e) => updateStep(i, { attachment_id: e.target.value ? Number(e.target.value) : null })}
                            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-fuchsia-500"
                          >
                            <option value="">— Seleziona allegato —</option>
                            {attachments.map((a) => (
                              <option key={a.id} value={a.id}>{a.filename}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Token hint (only for text steps) */}
                      {step.step_type === "text" && (
                        <p className="text-xs text-zinc-600 pl-11">
                          Variabili: <code className="bg-zinc-800 px-1 rounded text-fuchsia-400">{"{{nome}}"}</code>,{" "}
                          <code className="bg-zinc-800 px-1 rounded text-fuchsia-400">{"{{cognome}}"}</code>,{" "}
                          <code className="bg-zinc-800 px-1 rounded text-fuchsia-400">{"{{telefono}}"}</code>,{" "}
                          <code className="bg-zinc-800 px-1 rounded text-fuchsia-400">{"{{azienda}}"}</code>...
                        </p>
                      )}
                    </div>
                  );
                })
              )}

              {/* Add step buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => addStep("text")}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-zinc-700 hover:border-fuchsia-500 text-zinc-400 hover:text-fuchsia-400 rounded-xl transition-colors"
                  data-testid="add-text-step"
                >
                  <MessageSquare size={18} /> Messaggio
                </button>
                <button
                  onClick={() => addStep("image")}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-zinc-700 hover:border-fuchsia-500 text-zinc-400 hover:text-fuchsia-400 rounded-xl transition-colors"
                  data-testid="add-image-step"
                >
                  <Upload size={18} /> Allegato
                </button>
              </div>
            </div>
          </Card>

          {/* Attachments Manager */}
          <Card title="Allegati">
            <div className="space-y-4">
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-zinc-700 hover:border-fuchsia-500 text-zinc-400 hover:text-fuchsia-400 rounded-xl transition-colors disabled:opacity-50"
                >
                  {uploading ? <RefreshCw size={18} className="animate-spin" /> : <Upload size={18} />}
                  Carica File
                </button>
                <p className="text-xs text-zinc-500 text-center mt-2">JPG, PNG, WEBP, PDF fino a 10MB</p>
              </div>

              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {attachments.length === 0 ? (
                  <p className="text-sm text-zinc-600 text-center py-4">Nessun allegato caricato</p>
                ) : (
                  attachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 p-3 bg-zinc-900 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{a.filename}</p>
                        <p className="text-xs text-zinc-500">{formatSize(a.size_bytes)}</p>
                      </div>
                      <button onClick={() => deleteAttachment(a.id)} className="text-zinc-500 hover:text-red-400 p-1">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
